import type { DonorPolicyPresentation } from '../../api/voiceInvitationsApi';

export const DONOR_CONSENT_KINDS = [
  'voice_model_creation',
  'household_voice_share',
  'terms',
  'privacy',
] as const;

export type DonorConsentKind = typeof DONOR_CONSENT_KINDS[number];

export interface ApprovedDonorPolicy {
  kind: DonorConsentKind;
  policyId: string;
  version: string;
  text: string;
}

/**
 * Owner-approved production wording, 2026-08-29. Treat each identifier/version
 * as immutable: changed wording requires a new version, never an edit in place.
 */
export const APPROVED_DONOR_POLICIES: Record<DonorConsentKind, ApprovedDonorPolicy> = {
  voice_model_creation: {
    kind: 'voice_model_creation',
    policyId: 'luma-voice-model-creation',
    version: '2026-08-29',
    text: 'I consent to Luma using my recordings to create and operate an AI-generated voice model that sounds like me.',
  },
  household_voice_share: {
    kind: 'household_voice_share',
    policyId: 'luma-household-voice-share',
    version: '2026-08-29',
    text: 'I consent to sharing that voice model with this household so authorized household members can use it to create and listen to stories in Luma.',
  },
  terms: {
    kind: 'terms',
    policyId: 'luma-terms',
    version: '2026-08-05',
    text: 'I agree to Luma’s Terms of Service.',
  },
  privacy: {
    kind: 'privacy',
    policyId: 'luma-privacy',
    version: '2026-08-05',
    text: 'I acknowledge Luma’s Privacy Policy.',
  },
};

export function approvedDonorPolicies(
  policies: Record<string, DonorPolicyPresentation>,
): ApprovedDonorPolicy[] | null {
  const receivedKinds = Object.keys(policies).sort();
  const expectedKinds = [...DONOR_CONSENT_KINDS].sort();
  if (
    receivedKinds.length !== expectedKinds.length
    || receivedKinds.some((kind, index) => kind !== expectedKinds[index])
  ) return null;

  const approved: ApprovedDonorPolicy[] = [];
  for (const kind of DONOR_CONSENT_KINDS) {
    const received = policies[kind];
    const expected = APPROVED_DONOR_POLICIES[kind];
    if (
      !received
      || received.required !== true
      || received.policy_id !== expected.policyId
      || received.version !== expected.version
    ) return null;
    approved.push(expected);
  }
  return approved;
}
